// Builds a short Dutch explanation for call quality based on computed severities and displays
// quality: object from TaskEventsModal (overall, display, severity)
// context (optional): { direction, hangupBy, mos, pddMs }
export function getDutchQualityExplanation(quality, context = {}) {
  if (!quality) return null;
  const sev = quality.severity || {};
  const disp = quality.display || {};
  const bad = (s) => s === 'warn' || s === 'poor';
  const inboundBad = bad(sev.jitterInMax) || bad(sev.lossInPct);
  const outboundBad = bad(sev.jitterOutMax) || bad(sev.lossOutPct);
  const bothBad = inboundBad && outboundBad;

  let summary;
  if (bothBad) {
    summary = `De verbinding was instabiel in beide richtingen. Inkomend (Twilio → agent): jitter ${disp.jitterInMax}, verlies ${disp.lossInPct}. Uitgaand (agent → Twilio): jitter ${disp.jitterOutMax}, verlies ${disp.lossOutPct}. Dit kan zich uiten in wederzijdse haperingen, uitval van woorden en wisselende geluidskwaliteit.`;
  } else if (inboundBad) {
    summary = `De inkomende audiostroom naar de agent (Twilio → agent) was instabiel: jitter ${disp.jitterInMax} en/of pakketverlies ${disp.lossInPct}. De agent zal de beller waarschijnlijk haperend of robotachtig hebben gehoord, terwijl de beller de agent vermoedelijk normaal kon verstaan. Dit wijst meestal op een probleem op of nabij de agentzijde (wifi/last‑mile/pc) of het internetpad tussen Twilio en de agent, niet per se aan bellerszijde.`;
  } else if (outboundBad) {
    summary = `De uitgaande audiostroom vanaf de agent (agent → Twilio) was instabiel: jitter ${disp.jitterOutMax} en/of pakketverlies ${disp.lossOutPct}. De beller zal de agent waarschijnlijk haperend of robotachtig hebben gehoord, terwijl de agent de beller vermoedelijk normaal kon verstaan. Dit wijst meestal op een probleem met de uplink van de agent (bandbreedte/congestie) of het pad richting Twilio.`;
  } else if (quality.overall === 'good') {
    summary = 'De gemeten indicatoren vallen binnen normale marges. Indien er toch kwaliteitsproblemen zijn ervaren, kan het gaan om korte pieken die buiten de samenvatting of meetresolutie vallen.';
  } else {
    summary = 'Er zijn onvoldoende signalen om een duidelijke kwaliteitsconclusie te trekken. De beschikbare indicatoren geven geen duidelijke afwijkingen.';
  }

  // MOS note (when available and concerning)
  const mosNote = bad(sev.mos) && disp.mos ? ` MOS was ${disp.mos}, wat wijst op verminderde spraakkwaliteit.` : '';

  // PDD note (high setup delay)
  const pddNote = (sev.pddMs === 'warn' || sev.pddMs === 'poor') && disp.pddMs ? ` Opzetduur (PDD) was ${disp.pddMs}.` : '';

  // Possible causes (Mogelijke oorzaken)
  const commonCauses = [
    'Schommelingen of congestie op het internetpad.',
    'Wifi‑instabiliteit (zwak signaal, interferentie van andere netwerken of apparaten).',
    'Lokale systeembelasting die real‑time audio beïnvloedt (veel open tabs, schermopnames, videovergaderingen).',
    'VPN of netwerkinspectie die real‑time verkeer belemmert of vertraagt.',
  ];

  const inboundCauses = [
    'Last‑mile problemen aan agent‑zijde (wifi/kabel/pc) of pad van Twilio naar de agent.',
  ];

  const outboundCauses = [
    'Beperkte of fluctuerende uploadbandbreedte aan agent‑zijde (bijv. gelijktijdige uploads/cloud‑backups).',
  ];

  const causes = [];
  causes.push(...commonCauses);
  if (bothBad || inboundBad) causes.push(...inboundCauses);
  if (bothBad || outboundBad) causes.push(...outboundCauses);

  return {
    summary: summary + mosNote + pddNote,
    causes,
  };
}
